import {ACESFilmicToneMapping, Camera, PCFSoftShadowMap, Scene, WebGLRenderer} from "three";
import {ViewPortManager, ViewPortManagerStore} from "./view-port-manager.store";
import {Injectable, Injector} from "@angular/core";
import {RenderingStrategy} from "../service/rendering.service";
// ... muut importit kuten ACESFilmicToneMapping, PCFSoftShadowMap jne.
import {EffectComposer} from 'three/examples/jsm/postprocessing/EffectComposer.js';
import {RenderPass} from 'three/examples/jsm/postprocessing/RenderPass.js';
import {ShaderPass} from 'three/examples/jsm/postprocessing/ShaderPass.js';
import {FXAAShader} from 'three/examples/jsm/shaders/FXAAShader.js';

type RendererStoreState = {
    elementCount: number,
    renderer: WebGLRenderer,
    canvas: HTMLCanvasElement,
    animating: boolean,
    animationStarted: number | null,
    cycleTime: number,
    resizeObservers: ResizeObserver[],
    composer?: EffectComposer
}


/**
 * Ylläpitää renderöimistä varten tietoja kuten renderer, canvas, animoinnin tila...
 */
@Injectable({providedIn: 'root'})
export class RendererStore {

    private readonly viewPorts: ViewPortManager;

    private supersampleFactor: number = 1.5;

    constructor(private readonly injector: Injector) {
        this.viewPorts = this.injector.get(ViewPortManagerStore);
    }

    private readonly state: RendererStoreState = {
        elementCount: 0,
        renderer: null,
        canvas: null,
        animating: false,
        animationStarted: null, // epoch time to count how many milliseconds have passed
        cycleTime: 2000, //[ms] time to cycle through all viewPorts, default 1000ms
        resizeObservers: []
    }

    public get renderer(): WebGLRenderer {
        return this.state.renderer;
    }

    public get cycleTime(): number { return this.state.cycleTime; }

    public get animationStarted(): number | null { return this.state.animationStarted; }

    public get animating(): boolean { return this.state.animating; }

    public get canvas(): HTMLCanvasElement { return this.state.canvas; }

    public get canNotRender(): boolean { return !this.state.canvas || !this.state.renderer || this.state.elementCount < 1; }

    public get animateNecessary(): boolean {
        return this.viewPorts.getAll().some(
            viewPort => viewPort.renderingStrategy === 'animate');
    }

    public get renderingStrategy(): string {
        const viewPorts = this.viewPorts.getAll();
        if(viewPorts.some(p=>p.renderingStrategy === 'animate')) return RenderingStrategy.animate;
        if (viewPorts.some(p=>p.renderingStrategy === 'cycled')) return RenderingStrategy.cycled;
        return RenderingStrategy.static;
    }

    public get overCycleTime() {
        return this.animationStarted !== null
            ? Date.now() - this.animationStarted > this.cycleTime
            : false; //cycle not set
    }

    public initializeCanvas(canvas: HTMLCanvasElement): void {
        if(this.state.elementCount > 0) throw new Error('Only one canvas is supported currently');

        this.state.renderer = this.provideRenderer(canvas);
        this.state.canvas = canvas;
        this.state.elementCount++;
    }

    public resizeCanvas(width: number, height: number): void {
        if (this.state.canvas) {
            this.state.canvas.width = width;
            this.state.canvas.height = height;
            this.state.renderer.setSize(width, height);
            this.state.renderer.setPixelRatio((window.devicePixelRatio || 1) * this.supersampleFactor);
        } else {
            throw new Error('Canvas is not initialized');
        }
    }

    public removeCanvas(): void {
        if (this.state.elementCount < 1) throw new Error('No canvas to remove');

        this.state.canvas = undefined;
        this.state.renderer = undefined;
        this.state.elementCount--;
    }

    public resetAnimationCycle(value: number | null = null): void {
        this.state.animationStarted = value;
    }

    public setAnimationCycle(milliseconds: number): void {
        if(milliseconds < 1) { throw new Error('Cycle time must be greater than 0'); }

        this.state.cycleTime = milliseconds;
    }

    public animatingStatus(isAnimating: boolean, cycled: boolean = false): void {
        /*if(this.viewPorts.animateableViewPorts().length < 1) {
            this.state.animating = false;
            return;
        }*/

        //animation is set to be cycled
        if(isAnimating && cycled) { this.resetAnimationCycle(Date.now()) }

        this.state.animating = isAnimating;
    }

    private provideRenderer(canvas: HTMLCanvasElement): WebGLRenderer {
        const renderer = new WebGLRenderer(
            {
                canvas,
                antialias: true,
                context: canvas.getContext('webgl2'),
            }
        );
        renderer.setClearColor(0x000000, 0);
        renderer.setScissorTest(true);

        //renderer.setPixelRatio((window.devicePixelRatio || 1) * this.supersampleFactor);

        renderer.toneMapping = ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1;
        renderer.shadowMap.enabled = false; // Ota varjot käyttöön
        renderer.shadowMap.type = PCFSoftShadowMap;

        return renderer;
    }

    /**
     * Alustaa FXAA-postprosessoinnin.
     * Huom! Tämä metodi vaatii, että sinulla on käytössä scene ja camera.
     */
    public initializeFXAA(scene: Scene, camera: Camera): void {
        if (!this.state.renderer) {
            throw new Error('Renderer must be initialized before setting up FXAA.');
        }
        // Luodaan EffectComposer, joka käärii renderöintiputken
        const composer = new EffectComposer(this.state.renderer);
        // Renderöidään ensin perusscene
        const renderPass = new RenderPass(scene, camera);
        composer.addPass(renderPass);

        // Luodaan FXAA ShaderPass
        const fxaaPass = new ShaderPass(FXAAShader);

        // Aseta FXAA:lle resoluutiouniformit.
        // FXAAShader odottaa resoluution käänteisarvoja (1/width, 1/height)
        const canvas = this.state.canvas;
        const pixelRatio = this.state.renderer.getPixelRatio();
        fxaaPass.material.uniforms['resolution'].value.x = 1 / (canvas.clientWidth * pixelRatio);
        fxaaPass.material.uniforms['resolution'].value.y = 1 / (canvas.clientHeight * pixelRatio);

        composer.addPass(fxaaPass);

        // Tallenna composer tilaan, jotta sitä voidaan käyttää renderöinnissä
        this.state.composer = composer;
    }

    /**
     * Renderöi scenen käyttäen FXAA:ta, jos composer on alustettu.
     */
    public render(scene: Scene, camera: Camera): void {
        //this.initializeFXAA(scene, camera);
        this.state.renderer.render(scene, camera);
        //TODO: Selvitä miksi FXAA on liian raskas.
        /*if (this.state.composer) {
            this.state.composer.render();
        } else if (this.state.renderer) {
            this.state.renderer.render(scene, camera);
        } else {
            throw new Error('No renderer available');
        }*/
    }

    public setSuperSamplingFactor(factor: number): void {
        this.supersampleFactor = factor;
        if (this.state.renderer) {
            //HOX. Tämä korjaa mobiili asettelun. Joissain kohdissa nojataan canvas kokkoon, olisiko tämä parempi, tutki.
            const w = document.body.clientWidth; //this.state.canvas.width
            const h = document.body.clientHeight; //this.state.canvas.height

            this.resizeCanvas(w,h);
            this.resetAnimationCycle(Date.now());
        }
    }

    public clearCanvas(): void {

        const canvas = this.state?.canvas;
        if (!canvas) {
            console.warn('Canvas not available');
            return;
        }

        const gl = canvas.getContext('webgl2');
        if (!gl) {
            console.error('WebGL2 context not supported or failed to initialize.');
            return;
        }

        // Aseta tyhjennysväri eksplisiittisesti (tässä: valkoinen)
        gl.clearColor(0, 0, 0, 0);

        // (Tarvittaessa myös syvyys- ja stencil-bufferit)
        // gl.clearDepth(1.0);
        // gl.clearStencil(0);
        // gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);

        gl.clear(gl.COLOR_BUFFER_BIT);
    }

}



